import getConfig from 'next/config'

import { Types as AuthTypes } from '@grandvisionhq/auth'
import type { Context as CartContext } from '@grandvisionhq/carts-v2'
import type { CheckoutSettings, Routes, ShippingMethodsLogos } from '@grandvisionhq/checkout-v2'
import { Util as StoreUtils } from '@grandvisionhq/stores-v2'

import { CheckoutAddressForm } from '../components/checkout/forms/Address'
import ContactDetailsForm from '../components/checkout/forms/ContactDetails'
import PickupNameForm from '../components/checkout/forms/PickupName'
import AddressSummary from '../components/checkout/summaries/Address'
import ContactDetailsSummary from '../components/checkout/summaries/ContactDetails'
import PickupNameSummary from '../components/checkout/summaries/PickupName'

interface SettingsInput {
  cart: CartContext.CartContextModel['cart']
  shippingMethodsLogos?: ShippingMethodsLogos
}

const { publicRuntimeConfig } = getConfig()
const { orderTimeOut } = publicRuntimeConfig.checkout

// Configuration for forms required to fill in Checkout shipping step
export const stepShippingFormsConfig = {
  contactDetails: ContactDetailsForm,
  billingAddress: CheckoutAddressForm,
  deliveryAddress: CheckoutAddressForm,
  pickupName: PickupNameForm,
}

// Configuration for summaries on Checkout Overview step
export const stepOverviewSummariesConfig = {
  contactDetails: ContactDetailsSummary,
  billingAddress: AddressSummary,
  deliveryAddress: AddressSummary,
  pickupName: PickupNameSummary,
}

// TODO: make these pathes Danish instead of Swedish
const basePath = '/checkout'
export const routes: Routes = {
  shipping: {
    label: 'Levering',
    path: `${basePath}/levering`,
  },
  overview: {
    label: 'Oversigt',
    path: `${basePath}/oversigt`,
  },
  payment: {
    label: 'Betaling',
    path: `${basePath}/betaling`,
  },
  callback: {
    label: 'Callback',
    path: `${basePath}/callback`,
  },
}

export const getCheckoutConfig = ({
  cart,
  shippingMethodsLogos,
}: SettingsInput): CheckoutSettings => {
  let shippingDetails: CheckoutSettings['shippingDetails'] | undefined

  const optIns: AuthTypes.OptInItem[] = [
    {
      titleKey: 'account.optIn.marketingEmails',
      value: 'marketingEmailsOptIn',
    },
  ]

  if (cart && (cart.billingAddress || cart.shippingAddress || cart.customerDetails)) {
    shippingDetails = {
      ...(cart.billingAddress ? { billingAddress: cart.billingAddress } : {}),
      ...(cart.shippingAddress ? { deliveryAddress: cart.shippingAddress } : {}),
      ...(cart.customerDetails ? { customer: cart.customerDetails } : {}),
    }
  }

  return {
    account: {
      implicitAccountCreation: false,
      createAccountRequired: false,
    },
    consent: {
      implicitTermsAndConditions: true,
      optIns,
    },
    pickupPoints: {
      // Show additional city input field to query pickup points (default: false)
      showCityField: true,
    },
    shipping: {
      // Enforce an explicit shipping method selection (default: false)
      explicitShippingMethod: true,
    },
    shippingDetails: shippingDetails ? shippingDetails : {},
    shippingMethodsLogos: shippingMethodsLogos ? shippingMethodsLogos : {},
    storeFinder: {
      showUseMyLocation: true,
      searchWhitelist: ['storeName', 'postalCode', 'streetName', 'town'] as StoreUtils.SearchKey[],
    },
    ...(orderTimeOut ? { orderCreationTimeOutLimit: orderTimeOut } : {}),
    features: {
      isCompanyEnabled: false,
      isServiceStoreEnabled: false,
    },
    routes,
    payments: {
      applePay: {
        merchantName: 'Synoptik',
      },
    },
    logoVariant: 'main',
  }
}
